<template>
  <div class="flex h-12 p-2 rounded-lg bg-white shadow-toolbar" @wheel.ctrl.prevent @mousemove.prevent>
    <div
      v-if="userStore.userProfile" v-tooltip="{ text: userStore.userProfile.UserName, theme: { placement: 'left' } }"
      class="w-8 h-8 text-lg leading-[26px] text-center text-black rounded-full border-3 border-blue-500 mr-0.5"
      :style="{ backgroundColor: getMyColor() }" v-text="userStore.userProfile.initials"
    />
    <div
      v-for="user in listedUser" :key="user.UserName" v-tooltip="{ text: user.UserName, theme: { placement: 'left' } }"
      class="w-8 h-8 text-lg leading-[26px] text-center text-black rounded-full border-3 border-blue-500 mr-0.5"
      :style="{ backgroundColor: user.Color }" v-text="getInitials(user)"
    />
    <div
      v-if="remainingUserCount > 0" v-tooltip="{ text: remainingUsersList, theme: { placement: 'left' } }"
      class="w-8 h-8 text-lg leading-[26px] text-center text-white rounded-full border-3 border-blue-500 bg-blue-500 mr-0.5"
      v-text="`+${remainingUserCount}`"
    />
    <tx-button
      v-if="!isReadOnly && canInvite"
      v-tooltip="t('whiteboard.inviteUsers')" class="mr-1" :class="{ 'pl-1': users.length }" type="icon" :loading="loading"
      faicon="fa-light fa-share-from-square" @click="doInvite"
    />

    <!-- Share User Dialog -->
    <share-user-dialog ref="refShareUserDialog" v-model="selectedItems" @share="onShare" />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import type { IPresenceUser } from '../composables/presence'
import type Whiteboard from '../services/whiteboard'
import TxButton from '@/shared/components/TxButton.vue'
import type { IAccessibleItem } from '@/shared/components/ShareUserDialog.vue'
import ShareUserDialog from '@/shared/components/ShareUserDialog.vue'
import type { SharedUserGroupModel, SharedUserModel } from '@/api/t1/model/whiteboardModel'
import { getWhiteboardDetails, shareWhiteboard } from '@/api/t1/whiteboard'
import { useNotificationStore } from '@/store/notification'
import { useUserStore } from '@/store/userData'
import appConfig from '@/services/appConfig'
import { generateUserColor } from '@/services/utils'
import { whiteboardConstants } from '@/models/constants'

const props = defineProps<{
  users: IPresenceUser[]
  whiteboardId: number
  whiteboard?: Whiteboard
  permission: WhiteboardUserPermissionType
  canInvite: boolean
}>()

const { t } = useI18n()
const userStore = useUserStore()
const notificationStore = useNotificationStore()

const isReadOnly = computed(() => props.permission !== whiteboardConstants.userPermissions.write)

const loading = ref(false)
const refShareUserDialog = ref<InstanceType<typeof ShareUserDialog>>()
const selectedItems = ref<IAccessibleItem[]>([])

function getInitials(user: IPresenceUser) {
  return user.FirstName.substring(0, 1) + user.LastName.substring(0, 1)
}

function getMyColor() {
  return generateUserColor(userStore.userProfile!.UserName)
}

const listedUser = computed(() => {
  return props.users.slice(0, 8)
})

const remainingUserCount = computed(() => {
  return props.users.length - 8
})

const remainingUsersList = computed(() => {
  return props.users.slice(8).map(user => user.UserName).join('\n')
})

function doInvite() {
  selectedItems.value = []
  if (userStore.activeCatalog && props.whiteboardId) {
    loading.value = true
    getWhiteboardDetails(userStore.activeCatalog.CatalogCode, props.whiteboardId)
      .then((res) => {
        res.data.SharedUsersGroups.forEach((userGroup) => {
          const permission = userGroup.Write === 1
            ? whiteboardConstants.userPermissions.write as WhiteboardUserPermissionType
            : userGroup.Read === 1
              ? whiteboardConstants.userPermissions.read as WhiteboardUserPermissionType
              : userGroup.ReadAndComment === 1
                ? whiteboardConstants.userPermissions.readAndComment as WhiteboardUserPermissionType
                : whiteboardConstants.userPermissions.read as WhiteboardUserPermissionType // default
          selectedItems.value.push({
            id: userGroup.GroupId,
            type: 'group',
            name: userGroup.Name,
            subTitle: userGroup.AccountName,
            permission,
          })
        })
        res.data.SharedUsers.forEach((user) => {
          const permission = user.Write === 1
            ? whiteboardConstants.userPermissions.write as WhiteboardUserPermissionType
            : user.Read === 1
              ? whiteboardConstants.userPermissions.read as WhiteboardUserPermissionType
              : user.ReadAndComment === 1
                ? whiteboardConstants.userPermissions.readAndComment as WhiteboardUserPermissionType
                : whiteboardConstants.userPermissions.read as WhiteboardUserPermissionType // default
          selectedItems.value.push({
            id: user.UserId,
            type: 'user',
            name: `${user.FirstName} ${user.LastName}`,
            subTitle: user.UserName,
            permission,
          })
        })
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => {
        loading.value = false
        refShareUserDialog.value?.open()
      })
  }
  else {
    refShareUserDialog.value?.open()
  }
}

function onShare(items: IAccessibleItem[]) {
  if (userStore.activeCatalog && refShareUserDialog.value && props.whiteboardId) {
    const refShareUserDialogInstance = refShareUserDialog.value
    refShareUserDialogInstance.loading = true
    const sharedUsers: SharedUserModel[] = []
    const sharedUserGroups: SharedUserGroupModel[] = []
    items.forEach((item) => {
      if (item.type === 'group') {
        sharedUserGroups.push({
          GroupId: item.id,
          Read: item.permission === whiteboardConstants.userPermissions.read ? 1 : 0,
          Write: item.permission === whiteboardConstants.userPermissions.write ? 1 : 0,
          ReadAndComment: item.permission === whiteboardConstants.userPermissions.readAndComment ? 1 : 0,
        })
      }
      else if (item.type === 'user') {
        sharedUsers.push({
          UserId: item.id,
          Read: item.permission === whiteboardConstants.userPermissions.read ? 1 : 0,
          Write: item.permission === whiteboardConstants.userPermissions.write ? 1 : 0,
          ReadAndComment: item.permission === whiteboardConstants.userPermissions.readAndComment ? 1 : 0,
        })
      }
    })
    shareWhiteboard(userStore.activeCatalog.CatalogCode, props.whiteboardId, sharedUsers, sharedUserGroups)
      .then(async () => {
        notificationStore.addNotification({ message: t('shareUserDialog.shareSuccessfully'), type: 'Success' })
        await appConfig.DB!.whiteboards.update([userStore.activeCatalog!.CatalogCode, props.whiteboardId], { SharedUsers: sharedUsers, SharedUsersGroups: sharedUserGroups })
        if (props.whiteboard) {
          props.whiteboard.updateBoardMentionableList(items)
        }
        // userStore.doLoadData(['Whiteboards'])
      })
      .catch((e) => {
        console.error(e)
        refShareUserDialogInstance.errorMessage = t('general.unexpectedError')
      })
      .finally(() => {
        refShareUserDialogInstance.loading = false
        refShareUserDialogInstance.close()
      })
  }
}
</script>
